import { GlobalVariables } from "@/helpers/globalVariables"
import { ExtendedRecommendedJob } from "@/types"
import { DateTime } from "luxon"

export const GlobalFunctions = {
  /**
   * Convert keys from camelCase to snake_case
   */
  convertKeysCamelToSnakeCase(obj: any): any {
    if (obj === null || obj === undefined || typeof obj !== "object") {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => GlobalFunctions.convertKeysCamelToSnakeCase(item))
    }

    return Object.keys(obj).reduce((acc: { [key: string]: any }, key: string) => {
      const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase()
      acc[snakeKey] = GlobalFunctions.convertKeysCamelToSnakeCase(obj[key])
      return acc
    }, {})
  },
  delayInMiliseconds: (miliseconds = 60000) => {
    return new Promise((resolve) => {
      setTimeout(resolve, miliseconds)
    })
  },
  getWebsiteDomain: () => {
    return location.host === GlobalVariables.domains.app.prod
      ? GlobalVariables.domains.website.prod
      : GlobalVariables.domains.website.staging
  },
  getQublQuizIdForCoaching: () => {
    return location.host === GlobalVariables.domains.app.prod
      ? GlobalVariables.qublQuizIds.prod.coaching
      : GlobalVariables.qublQuizIds.staging.coaching
  },
  getQublQuizIdForJobQuiz: () => {
    return location.host === GlobalVariables.domains.app.prod
      ? GlobalVariables.qublQuizIds.prod.jobQuiz
      : GlobalVariables.qublQuizIds.staging.jobQuiz
  },
  getQublQuizIdForJobTest: () => {
    return location.host === GlobalVariables.domains.app.prod
      ? GlobalVariables.qublQuizIds.prod.jobTest
      : GlobalVariables.qublQuizIds.staging.jobTest
  },
  getQublQuizIdForTestRetake: () => {
    return location.host === GlobalVariables.domains.app.prod
      ? GlobalVariables.qublQuizIds.prod.testRetake
      : GlobalVariables.qublQuizIds.staging.testRetake
  },
  getQublQuizId: () => {
    if (location.pathname.includes(GlobalVariables.urls.report.children.testRetake.path)) {
      return GlobalFunctions.getQublQuizIdForTestRetake()
    }

    if (location.pathname.includes(GlobalVariables.urls.jobQuiz.path)) {
      return GlobalFunctions.getQublQuizIdForJobQuiz()
    }

    // Default to jobTest
    return GlobalFunctions.getQublQuizIdForJobTest()
  },
  getQublStyleForJobTest: () => {
    return GlobalVariables.qublQuizStyles.jobTest
  },
  getQublStyleForJobQuiz: () => {
    return GlobalVariables.qublQuizStyles.jobQuiz
  },
  getQublStyleForTestRetake: () => {
    return GlobalVariables.qublQuizStyles.testRetake
  },
  getQublStyle: () => {
    if (location.pathname.includes(GlobalVariables.urls.report.children.testRetake.path)) {
      return GlobalFunctions.getQublStyleForTestRetake()
    }

    if (location.pathname.includes(GlobalVariables.urls.jobQuiz.path)) {
      return GlobalFunctions.getQublStyleForJobQuiz()
    }

    // Default to jobTest
    return GlobalFunctions.getQublStyleForJobTest()
  },
  getUserSourceData: function () {
    const userSource = {} as { [key: string]: string }

    try {
      // Retrieve url parameters from session storage
      GlobalVariables.userSourceKeys.forEach((key) => {
        const value = sessionStorage.getItem(key)
        if (value) userSource[key] = value
      })

      // Add Cookies
      const fbc = GlobalFunctions.retrieveCookie("_fbc")
      const fbp = GlobalFunctions.retrieveCookie("_fbp")
      const ttp = GlobalFunctions.retrieveCookie("_ttp")
      if (fbc) userSource["fbc"] = fbc
      if (fbp) userSource["fbp"] = fbp
      if (ttp) userSource["ttp"] = ttp

      // Add affiliate code
      const affiliateCode = sessionStorage.getItem("affiliate_code")
      if (affiliateCode) userSource["affiliate_code"] = affiliateCode

      return userSource
    } catch (error) {
      console.error("Error retrieving user source data: ", error)
      return userSource
    }
  },
  isExtendedRecommendedJobType: (obj: any): obj is ExtendedRecommendedJob => {
    return typeof obj === "object" && obj !== null && "jobRank" in obj
  },
  numberAsCurrency: (number: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(number)
  },
  numberWithoutDecimals: (number: number) => {
    const numberAsString = typeof number === "number" ? number.toString() : number
    return numberAsString.includes(".") ? numberAsString.split(".")[0] : numberAsString
  },
  redirectToPage: ({ baseUrl, pageUrl, queryUrl = "" }: any) => {
    const finalUrl = `${baseUrl}${pageUrl}${queryUrl}`
    // Simulate an HTTP redirect
    // Note: The difference between href and replace, is that replace() removes the URL of the current document from the document history, meaning that it is not possible to use the "back" button to navigate back to the original document.
    window.location.replace(finalUrl)
  },
  retrieveCookie(cookieName: string) {
    const cookies = document.cookie.split(";")

    for (let i = 0; i < cookies.length; i++) {
      const [name, value] = cookies[i].trim().split("=")
      if (name === cookieName) {
        return value
      }
    }

    return null
  },
  setToLastDateTimeOfMonth: () => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()

    // Set the date object to the first day of the NEXT month.
    now.setFullYear(currentYear, currentMonth + 1, 1)
    now.setHours(0, 0, 0, 0)
    // Subtract one millisecond. This goes back to the absolute last moment of the previous month.
    now.setMilliseconds(now.getMilliseconds() - 1)

    return now
  },
  scrollInToElement: (elementSelector: string) => {
    if (!elementSelector) return
    const element = document.querySelector(elementSelector)
    if (element) element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" })
  },
  storeUserSourceData: function (): void {
    const urlQueryString = window.location.search
    const urlParameters = new URLSearchParams(urlQueryString)

    const storableKeys = GlobalVariables.userSourceKeys

    // Store url parameters in session storage if not already stored
    urlParameters.forEach((value, key) => {
      if (storableKeys.includes(key) && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, value)
      }
    })

    // affiliate_code is a special case where it is received as a hash, not a query parameter
    const affiliateCode = window.location.hash.split("#")[1]
    if (affiliateCode && !sessionStorage.getItem("affiliate_code")) {
      sessionStorage.setItem("affiliate_code", affiliateCode)
    }
  },
  toTitleCase: (str: string) => {
    if (!str) return ""
    const lowerCase = str.toLowerCase()
    return lowerCase.replace(/(^|\s)\S/g, (letter) => letter.toUpperCase())
  },
  /**
   * Used to create date object of the exact dateString at 00:00:00 hours at local timezone.
   * dateString must match "YYYY-MM-DD"
   * Used for BE request date filters
   */
  convertStringToLocaleDate: (dateString: string) => {
    const splitDates = dateString.split("-")
    const dateToReturn = new Date(dateString)

    dateToReturn.setFullYear(parseInt(splitDates[0]))
    dateToReturn.setDate(parseInt(splitDates[2]))
    dateToReturn.setMonth(parseInt(splitDates[1]) - 1)
    dateToReturn.setHours(0)
    dateToReturn.setMinutes(0)
    dateToReturn.setSeconds(0)

    return dateToReturn
  },

  calculateDateDifference: (lesserDate: Date, greaterDate: Date) => {
    const differenceForTime = greaterDate.getTime() - lesserDate.getTime()
    const minuteDifference = Math.round(differenceForTime / (60 * 1000))
    const greaterDayStart = DateTime.fromJSDate(greaterDate).setZone("local").startOf("day")
    const lesserDayStart = DateTime.fromJSDate(lesserDate).setZone("local").startOf("day")
    const diff = greaterDayStart.diff(lesserDayStart, ["days"])
    const dayDifference = diff.days
    return { day: dayDifference, minute: minuteDifference }
  }
}
